/** The files a question carried, under it in the chat. A chip opens the
 *  file in a dialog (image, text, or facts + download). */
import CloseIcon from '@mui/icons-material/Close';
import { Chip, Dialog, DialogContent, DialogTitle, IconButton, Stack, Typography } from '@mui/material';
import { useState } from 'react';

import type { DiagnoseAttachment } from '@/api/diagnoseAttachments';
import { formatBytes } from '@/utils/formatBytes';

import { AttachmentPreview } from './AttachmentPreview';
import { KindIcon } from './kind';

export function AttachmentPreviewDialog({
  taskId,
  attachment,
  onClose,
}: {
  taskId: string;
  attachment: DiagnoseAttachment | null;
  onClose: () => void;
}) {
  return (
    <Dialog open={attachment != null} onClose={onClose} fullWidth maxWidth="md" aria-labelledby="att-preview-title">
      {attachment && (
        <>
          <DialogTitle id="att-preview-title" sx={{ pr: 6 }}>
            <Typography variant="h6" component="span" sx={{ wordBreak: 'break-all' }}>
              {attachment.filename}
            </Typography>
            <IconButton aria-label="Close" onClick={onClose} sx={{ position: 'absolute', right: 8, top: 8 }}>
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent dividers>
            <AttachmentPreview taskId={taskId} attachment={attachment} />
          </DialogContent>
        </>
      )}
    </Dialog>
  );
}

export function AttachmentChips({
  attachments,
  taskId,
}: {
  attachments: DiagnoseAttachment[];
  /** The turn's task (any thread member serves the thread's files). */
  taskId: string;
}) {
  const [open, setOpen] = useState<DiagnoseAttachment | null>(null);
  return (
    <>
      <Stack direction="row" flexWrap="wrap" useFlexGap spacing={0.75} aria-label="Attached files">
        {attachments.map((a) => (
          <Chip
            key={a.id}
            size="small"
            variant="outlined"
            icon={<KindIcon kind={a.kind} contentType={a.content_type} fontSize="small" />}
            label={`${a.filename} · ${formatBytes(a.size)}`}
            onClick={() => setOpen(a)}
            sx={{ maxWidth: 320 }}
          />
        ))}
      </Stack>
      <AttachmentPreviewDialog taskId={taskId} attachment={open} onClose={() => setOpen(null)} />
    </>
  );
}
